<template>
  <div ref="container" class="inline-block relative">
    <div v-if="!isLoaded" class="w-[90px] h-[90px] flex items-center justify-center bg-slate-200 rounded animate-pulse">
      <span v-if="!isImage"> {{ fileTypeToString(image) }} </span>
    </div>

    <picture v-else-if="isImage" class="cursor-pointer" @click="openFile">
      <source :srcset="imageSrc" type="image/webp" />
      <img
        :src="imageSrc"
        :alt="altText"
        loading="lazy"
        class="object-cover w-[90px] h-[90px] rounded hover:scale-105 transition-transform"
        @error="onImageError"
      />
    </picture>

    <div v-else class="w-[90px] h-[90px] flex items-center justify-center bg-slate-200 rounded cursor-pointer" @click="openFile">
      {{ fileTypeToString(image) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { fileTypeToString, getFileStyle } from '@/utils'

const props = defineProps({
  image: { type: String, required: true },
  altText: { type: String, default: '' },
  noLightbox: { type: Boolean, default: false },
  imgeStyle: { type: String, default: '200x200' }
})

const imageSrc = ref('')
const isLoaded = ref(false)
const container = useTemplateRef<HTMLDivElement>('container')

const isImage = computed(() => {
  const ext = props.image.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)
  return ext ? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext[1].toLowerCase()) : false
})

useIntersectionObserver(
  container,
  ([{ isIntersecting }]) => {
    if (isIntersecting) loadImage()
  },
  { threshold: 0.1 }
)

const loadImage = () => {
  getFileStyle({ url: props.image, style: props.imgeStyle as any })
  .then((src) => {
    imageSrc.value = src || props.image
    isLoaded.value = true
  })
  .catch(() => {
    imageSrc.value = props.image
    isLoaded.value = true
  })
}

const onImageError = () => {
  imageSrc.value = props.image
  isLoaded.value = true
  console.log('Error loading image', props.image)
}

const openFile = () => {
  if (props.noLightbox) window.open(props.image, '_blank')
}
</script>
